import { ParsedBusinessResult } from './parseResponse';
import { BusinessSubmissionRecovery } from './contract';

export type SubmitPhase = 'idle' | 'in_flight' | 'submitted' | 'rejected'; // rejected: definitively refused for this identity.

export type SubmitBlocker =
    | 'no_eligible_result' // Nothing eligible to submit: idle, waiting, invalid, failure, unknown or already-submitted result.
    | 'lookup_in_flight' // The lookup is still loading.
    | 'cui_mismatch' // The field no longer matches the verified CUI.
    | 'verification_expired'
    | 'session_unusable'
    | 'submit_in_flight'
    | 'retry_wait' // An uncertain or retryable submit answer: wait before the unchanged retry.
    | 'submit_rejected' // The server definitively refused this identity. Saved submissions cannot be replaced.
    | 'already_submitted';

export interface SubmitEligibilityInput {
    rawCui: string; // Exactly what the field shows now.
    lookup: {
        phase: 'idle' | 'waiting' | 'invalid' | 'loading' | 'done';
        cui?: string;
        result?: ParsedBusinessResult;
    };
    sessionUsable: boolean; // Supplied by the page (session errors, reload state).
    submitPhase: SubmitPhase; // Owned by the submit step.
    now: number; // Epoch ms, passed in so the rule stays pure.
    recovery?: BusinessSubmissionRecovery | null; // Already accepted: only the original session lifetime applies.
}

/**
 * Every reason the business submit must stay disabled; an empty list means it
 * is allowed. The server re-checks all of this at submit, so this is a UX guard
 * and never the authority.
 */
export const getSubmitBlockers = (input: SubmitEligibilityInput): SubmitBlocker[] => {
    const { rawCui, lookup, sessionUsable, submitPhase, now } = input;
    const blockers: SubmitBlocker[] = [];

    if (lookup.phase === 'loading') {
        blockers.push('lookup_in_flight');
    }

    const result = lookup.result;
    const eligible =
        lookup.phase === 'done' &&
            result?.kind === 'eligible' &&
            result.response.canSubmitBusiness === true
            ? result.response
            : null;

    if (input.recovery) {
        if (rawCui !== input.recovery.business.cui) blockers.push('cui_mismatch');
    } else if (!eligible) {
        blockers.push('no_eligible_result');
    } else {
        // Exact comparison, no trimming. The answer's own CUI must match too, never invoiceIdentifier.
        if (rawCui !== lookup.cui || rawCui !== eligible.company.tax.cui) {
            blockers.push('cui_mismatch');
        }
        // The expiry instant itself already counts as expired; an unreadable date never allows.
        if (!(now < Date.parse(eligible.verification.expiresAt))) {
            blockers.push('verification_expired');
        }
    }

    if (!sessionUsable) {
        blockers.push('session_unusable');
    }
    if (submitPhase === 'in_flight') {
        blockers.push('submit_in_flight');
    }
    if (submitPhase === 'rejected') {
        blockers.push('submit_rejected');
    }
    if (submitPhase === 'submitted') {
        blockers.push('already_submitted');
    }
    return blockers;
};

export const canSubmit = (input: SubmitEligibilityInput): boolean => getSubmitBlockers(input).length === 0;
